/* eslint-disable @next/next/no-img-element */
// RODAPÉ "Minimalista duas faixas" (rodape = 5): fundo escuro derivado do tema (cor3 com
// fallback near-black), duas faixas separadas por uma divisória fina.
//  Faixa 1: logo à ESQUERDA + navegação horizontal à DIREITA (itens do menu do tenant).
//  Faixa 2: copyright + CRECI à ESQUERDA (dados do tenant) + selo/vazio à DIREITA.
// Mantém a injeção obrigatória de tbl_sites.html_rodape (igual aos rodapés atuais).
// TUDO VARIÁVEL: logo/menu/CRECI/empresa = dados do tenant; cores = site.corN com contraste
// adaptativo (ensureReadableTextColor/isColorLight). Nenhum texto/cor hardcoded.
// Default export = ErrorBoundary com fallback pro rodapé padrão (AboutPieces1).
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { ensureReadableTextColor, isColorLight } from '../../../utils/FunctionsGlobals';
import { trackMetaEvent } from '../../../utils/metaPixel';
import { appendCodsiteQuery } from '../../../utils/url';
import { useAuth, useSiteRegiao } from '../../context/Info';
import Popular from '../populares/Popular';
import styles from './EditorialFooter.module.css';
import FiliaisRodape from '../filiais-rodape/FiliaisRodape';
import { SeloImobtotalItem } from '../selo-imobtotal/SeloImobtotal';

interface IEditorialFooter {
    site: any;
    allCities?: any;
    origin?: any;
    codsite?: any;
    isMobile?: boolean;
    favorito?: any;
    allRodape?: any;
    hidePopular?: boolean;
    fallback?: React.ReactNode;
}

type NavItem = { label: string; href: string; external?: boolean };

const EditorialFooterInner: React.FC<IEditorialFooter> = ({ site: siteProp, origin, codsite, allRodape, hidePopular }) => {
    const site = useSiteRegiao(siteProp);
    const { allDataHome } = useAuth();
    const router = useRouter();
    const codsiteQuery = Array.isArray(router.query?.codsite) ? router.query?.codsite[0] : router.query?.codsite;
    const [isMounted, setIsMounted] = useState(false);
    useEffect(() => setIsMounted(true), []);
    const withCodsite = (href: string) => (isMounted ? appendCodsiteQuery(href, codsite ?? codsiteQuery, origin) : href);
    const trackFooter = (nome: string, cat: string) => () =>
        trackMetaEvent(site, 'ViewContent', { content_name: nome, content_category: cat });

    // fundo escuro derivado do tema (cor3) com fallback near-black + texto adaptativo real.
    const corFooter = site?.cor3 || '#0d0d0d';
    const corText = ensureReadableTextColor(isColorLight(corFooter) ? site?.cor13 : site?.cor11, corFooter) || '#ffffff';

    // navegação: transações + menus do site + blog.
    const navItems: NavItem[] = [];
    (site?.transacoes || []).forEach((t: string) => {
        if (!t) return;
        const low = String(t).toLowerCase();
        const label = low === 'venda' ? 'Comprar' : low === 'aluguel' ? 'Alugar' : low === 'temporada' ? 'Temporada' : t;
        navItems.push({ label, href: `/imoveis/${low}/-/-/-/-` });
    });
    (allDataHome?.menus || [])
        .filter((m: any) => m?.Disponivel && m?.titulo)
        .slice(0, 6)
        .forEach((m: any) => {
            navItems.push({ label: m.titulo, href: m?.link ? m.link : `/menu/${m.titulo}`, external: Boolean(m?.link && !String(m.link).startsWith('/')) });
        });
    if (allDataHome?.blogs?.[0]?.id) navItems.push({ label: 'Blog', href: '/blog' });

    const logoSrc = site?.url_logo ? `${site?.url_logo}?v=${site?.contador_marca || 0}` : '';
    const empresa = site?.Dados?.empresa || '';
    const creci = site?.Dados?.creci;

    return (
        <>
        {/* Idem MegaFooter: o bloco de pesquisas populares faltava neste rodapé. */}
        {!hidePopular && <Popular site={site} allRodape={allRodape} codsite={codsite} origin={origin}/>}
        {/* BUILDER: âncora estável do bloco "rodape". */}
        <footer className={styles.footer} data-builder-section="rodape" style={{ background: corFooter, color: corText }}>
            <div className={styles.inner}>
                {/* faixa 1: logo + nav */}
                <div className={styles.band1}>
                    <Link prefetch={false} href={withCodsite('/')} className={styles.logoLink} title={`Imobiliária ${empresa}`}>
                        {logoSrc ? (
                            <Image src={logoSrc} alt={`Imobiliária ${empresa}`} className={styles.logo} width={220} height={80} unoptimized />
                        ) : (
                            <span className={styles.logoText} style={{ color: corText }}>{empresa}</span>
                        )}
                    </Link>

                    <nav className={styles.nav} aria-label="Navegação do rodapé">
                        {navItems.map((item, idx) =>
                            item.external ? (
                                <a key={idx} href={item.href} className={styles.navLink} style={{ color: corText }} rel="noopener noreferrer" onClick={trackFooter(item.label, 'footer_editorial')}>
                                    {item.label}
                                </a>
                            ) : (
                                <Link prefetch={false} key={idx} href={withCodsite(item.href)} className={styles.navLink} style={{ color: corText }} onClick={trackFooter(item.label, 'footer_editorial')}>
                                    {item.label}
                                </Link>
                            )
                        )}
                    </nav>
                </div>

                {/* MULTI-FILIAIS: endereços de TODAS as filiais (null fora do modo popup). */}
                <FiliaisRodape site={site} fundo={corFooter} cor={corText} />

                <div className={styles.divider} style={{ borderColor: corText }} aria-hidden="true" />

                {/* faixa 2: copyright + CRECI / selo */}
                <div className={styles.band2}>
                    <span className={styles.legal} style={{ color: corText }}>
                        © {new Date().getFullYear()} {empresa}. Todos os direitos reservados.
                        {creci ? <span className={styles.creci}> · CRECI {creci}</span> : null}
                    </span>
                    {site?.html_rodape ? (
                        <span className={styles.htmlRodape} style={{ color: corText }} dangerouslySetInnerHTML={{ __html: site?.html_rodape }} />
                    ) : null}
                    {/* Selo ImobTotal (28/09): linha própria embaixo do ©, alinhada à esquerda — à direita
                        (lugar que o desenho reservou) o botão flutuante do WhatsApp cobria o texto. */}
                    <SeloImobtotalItem site={site} host={origin} alinhar="esquerda" linhaPropria fonte="13px" />
                </div>
            </div>
        </footer>
        </>
    );
};

class EditorialFooter extends React.Component<IEditorialFooter, { hasError: boolean }> {
    constructor(props: IEditorialFooter) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch() {
        // silencioso: degrada pro rodapé padrão sem quebrar a página.
    }
    render() {
        const { fallback, site } = this.props;
        if (this.state.hasError || !site) return fallback ?? null;
        return <EditorialFooterInner {...this.props} />;
    }
}

export default EditorialFooter;
